import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useNotify, useRefresh } from 'react-admin';
import { LinkCallerDialog } from './LinkCallerDialog';
import { fetchLinkSource, undoCallerLink } from './api';
import { LinkSource } from './linkCallerFormat';

/**
 * "Link to…" next to Qualify / Disqualify (T-189). `?link=1` on the lead's URL
 * opens it straight away — that is what the Slack call card's button links to.
 */
export function LeadLinkButton({ leadId }: { leadId: string }) {
  const refresh = useRefresh();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (searchParams.get('link') !== '1') return;
    setOpen(true);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('link');
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-[13px] text-blue-600 hover:text-blue-700 font-medium border border-blue-300 rounded px-2.5 py-1 hover:bg-blue-50 transition-colors"
        title="This caller is someone on a case or lead we already have"
      >
        Link to…
      </button>
      <LinkCallerDialog source={open ? { leadId } : null} onClose={() => setOpen(false)} onLinked={refresh} />
    </>
  );
}

/** On a stray lead that was linked away: where it went, and Undo. */
export function LeadLinkedBanner({ leadId, stage }: { leadId: string; stage: string }) {
  const notify = useNotify();
  const refresh = useRefresh();
  const [src, setSrc] = useState<LinkSource | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (stage !== 'closed_lost') {
      setSrc(null);
      return;
    }
    let live = true;
    void fetchLinkSource({ leadId })
      .then((s) => live && setSrc(s))
      .catch(() => live && setSrc(null));
    return () => {
      live = false;
    };
  }, [leadId, stage]);

  const link = src?.activeLink;
  if (!link) return null;

  const undo = async () => {
    setBusy(true);
    try {
      await undoCallerLink(link.id);
      notify('Link undone — this lead is back as it was', { type: 'info' });
      setSrc(null);
      refresh();
    } catch (e) {
      notify(e instanceof Error ? e.message : "Couldn't undo", { type: 'warning' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 rounded border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-[12px] text-blue-900 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-200">
      Linked to{' '}
      {link.funeralId ? (
        <RouterLink className="underline" to={`/funerals/${link.funeralId}/show`}>
          case #{link.funeralId}
        </RouterLink>
      ) : (
        <RouterLink className="underline" to={`/leads/${link.leadId}/show`}>
          another lead
        </RouterLink>
      )}
      {link.byName ? ` by ${link.byName}` : ''}.{' '}
      <button className="underline font-medium" disabled={busy} onClick={() => void undo()}>
        Undo
      </button>
    </div>
  );
}
